import React, { useEffect, useState } from "react";
import axios from "../services/api";
import toast from "react-hot-toast";
import { FaTrash, FaEdit } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({
    id: null,
    name: "",
    email: "",
    password: "",
    role: "user",
    company_id: "",
    is_active: true,
  });
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/users/");
      setUsers(res.data);
    } catch {
      toast.error("Failed to fetch users");
    }
    setLoading(false);
  };

  const fetchCompanies = async () => {
    try {
      const res = await axios.get("/companies/");
      setCompanies(res.data.filter((c) => c.is_active));
    } catch {
      toast.error("Failed to load companies");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await axios.put(`/users/${form.id}`, form);
        toast.success("User updated successfully!");
      } else {
        await axios.post("/users/", form);
        toast.success("User created successfully!");
      }
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error("Operation failed");
    }
    setLoading(false);
  };

  const handleEdit = (user) => {
    setForm({ ...user, password: "", id: user.id });
    setEditing(true);
  };

  const confirmDelete = (userId) => setDeleteTarget(userId);

  const performDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(`/users/${deleteTarget}`);
      toast.success("User deleted");
      fetchUsers();
    } catch(err) {
      toast.error("Failed to delete user");
    }
    setDeleteTarget(null);
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      id: null,
      name: "",
      email: "",
      password: "",
      role: "user",
      company_id: "",
      is_active: true,
    });
    setEditing(false);
  };

  const filteredUsers = users.filter((u) => {
    const matchSearch =
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = !filterRole || u.role === filterRole;
    const matchCompany = !filterCompany || u.company_id === filterCompany;
    return matchSearch && matchRole && matchCompany;
  });

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">👥 Manage Users</h2>

      {/* Filters */}
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <input
          type="text"
          placeholder="Search by name or email"
          className="border p-2 rounded"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="border p-2 rounded"
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
        >
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
        </select>
        <select
          className="border p-2 rounded"
          value={filterCompany}
          onChange={(e) => setFilterCompany(e.target.value)}
        >
          <option value="">All Companies</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-100 p-4 rounded mb-6"
      >
        <input
          type="text"
          placeholder="Name"
          className="border p-2 rounded"
          value={form.name}
          required
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <input
          type="email"
          placeholder="Email"
          className="border p-2 rounded"
          value={form.email}
          required
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        {!editing && (
          <input
            type="password"
            placeholder="Password"
            className="border p-2 rounded"
            value={form.password}
            required
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        )}
        <select
          className="border p-2 rounded"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
        >
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
        <select
          className="border p-2 rounded"
          value={form.company_id}
          onChange={(e) => setForm({ ...form, company_id: e.target.value })}
        >
          <option value="">Select Company</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <label className="flex items-center px-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="mr-2"
          />
          Active
        </label>
        <button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded col-span-1 md:col-span-2"
          disabled={loading}
        >
          {editing ? "Update User" : "Create User"}
        </button>
      </form>

      {/* Table */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60 z-10">
            <div className="h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        <table className="min-w-full bg-white shadow rounded overflow-hidden">
          <thead className="bg-gray-100 text-gray-700 text-sm">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Company</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u.id} className="border-t hover:bg-gray-50 text-sm">
                <td className="px-4 py-2">{u.name}</td>
                <td>{u.email}</td>
                <td>{u.role}</td>
                <td>{companies.find((c) => c.id === u.company_id)?.name || u.company_id}</td>
                <td>{u.is_active ? "✅" : "❌"}</td>
                <td className="flex gap-3 px-4 py-2">
                  <button
                    onClick={() => handleEdit(u)}
                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <FaEdit /> Edit
                  </button>
                  <button
                    onClick={() => confirmDelete(u.id)}
                    className="text-red-600 hover:text-red-800 flex items-center gap-1"
                  >
                    <FaTrash /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-lg p-6 shadow-xl w-80"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <h3 className="text-lg font-semibold mb-3 text-gray-800">
                Confirm Deletion
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete this user?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={performDelete}
                  className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
